import { createAction, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosPrivate } from '../hooks/axiosInstance';

// Create a new blog post
export const createBlog = createAsyncThunk(
  'blog/createBlog',
  async (blogData, thunkAPI) => {
    try {
      // Handle FormData for file uploads
      const config = {
        headers: {
          'Content-Type': blogData instanceof FormData ? 'multipart/form-data' : 'application/json',
        }
      };
      
      const response = await axiosPrivate.post('/admin/blog', blogData, config);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Get all blogs for the current user
export const getBlogs = createAsyncThunk(
  'blog/getBlogs',
  async (params = {}, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams ? `/admin/blogs?${queryParams}` : '/admin/blogs';
      const response = await axiosPrivate.get(url);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Get a single blog post
export const getBlog = createAsyncThunk(
  'blog/getBlog',
  async (blogId, thunkAPI) => {
    try {
      const response = await axiosPrivate.get(`/admin/blog/${blogId}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Update a blog post
export const updateBlog = createAsyncThunk(
  'blog/updateBlog',
  async ({ blogId, blogData }, thunkAPI) => {
    try {
      const config = {
        headers: {
          'Content-Type': blogData instanceof FormData ? 'multipart/form-data' : 'application/json',
        }
      };
      
      const response = await axiosPrivate.put(`/admin/blog/${blogId}`, blogData, config);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Delete a blog post
export const deleteBlog = createAsyncThunk(
  'blog/deleteBlog',
  async (blogId, thunkAPI) => {
    try {
      await axiosPrivate.delete(`/admin/blog/${blogId}`);
      return blogId; // Return the ID for removal from state
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Delete all blogs
export const deleteAllBlogs = createAsyncThunk(
  'blog/deleteAllBlogs',
  async (_, thunkAPI) => {
    try {
      const response = await axiosPrivate.delete('/admin/blogs');
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Get blog statistics
export const getBlogStats = createAsyncThunk(
  'blog/getBlogStats',
  async (_, thunkAPI) => {
    try {
      const response = await axiosPrivate.get('/admin/blog-stats');
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Increment blog views (public endpoint - requires API key)
export const incrementBlogViews = createAsyncThunk(
  'blog/incrementBlogViews',
  async (blogId, thunkAPI) => {
    try {
      const response = await axiosPrivate.patch(`/admin/blog/${blogId}/view`);
      return { blogId, views: response.data.views };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.msg || 'Failed to increment views');
    }
  }
);

// Clear error action
export const clearError = createAction('blog/clearError');

// Clear success action
export const clearSuccess = createAction('blog/clearSuccess');

export const blogSlice = createSlice({
  name: 'blog',
  initialState: {
    blogs: [],
    currentBlog: null,
    stats: {
      totalBlogs: 0,
      publishedBlogs: 0,
      draftBlogs: 0,
      totalViews: 0,
      totalLikes: 0,
      publishRate: 0,
      avgReadTime: 0
    },
    pagination: {
      currentPage: 1,
      totalPages: 1,
      total: 0
    },
    loading: false,
    creating: false,
    updating: false,
    deleting: false,
    success: false,
    error: null,
    filters: {
      status: 'all',
      category: 'all',
      search: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    }
  },
  reducers: {
    setCurrentBlog: (state, action) => {
      state.currentBlog = action.payload;
    },
    clearCurrentBlog: (state) => {
      state.currentBlog = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: 'all',
        category: 'all',
        search: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };
    },
    updateBlogInList: (state, action) => {
      const index = state.blogs.findIndex(blog => blog._id === action.payload._id);
      if (index !== -1) {
        state.blogs[index] = action.payload;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Create Blog
      .addCase(createBlog.pending, (state) => {
        state.creating = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createBlog.fulfilled, (state, action) => {
        state.creating = false;
        state.success = true;
        state.blogs.unshift(action.payload); // Add to beginning of array
      })
      .addCase(createBlog.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Get Blogs
      .addCase(getBlogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBlogs.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both paginated and non-paginated responses
        if (action.payload.blogs) {
          state.blogs = action.payload.blogs;
          state.pagination = {
            currentPage: action.payload.currentPage || 1,
            totalPages: action.payload.totalPages || 1,
            total: action.payload.total || action.payload.blogs.length
          };
        } else {
          state.blogs = action.payload;
          state.pagination = {
            currentPage: 1,
            totalPages: 1,
            total: action.payload.length
          };
        }
      })
      .addCase(getBlogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Get Single Blog
      .addCase(getBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBlog.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBlog = action.payload;
      })
      .addCase(getBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Update Blog
      .addCase(updateBlog.pending, (state) => {
        state.updating = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateBlog.fulfilled, (state, action) => {
        state.updating = false;
        state.success = true;
        state.currentBlog = action.payload;
        // Update blog in the list
        const index = state.blogs.findIndex(blog => blog._id === action.payload._id);
        if (index !== -1) {
          state.blogs[index] = action.payload;
        }
      })
      .addCase(updateBlog.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Delete Blog
      .addCase(deleteBlog.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteBlog.fulfilled, (state, action) => {
        state.deleting = false;
        state.success = true;
        // Remove blog from list
        state.blogs = state.blogs.filter(blog => blog._id !== action.payload);
        // Clear current blog if it was deleted
        if (state.currentBlog && state.currentBlog._id === action.payload) {
          state.currentBlog = null;
        }
      })
      .addCase(deleteBlog.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Delete All Blogs
      .addCase(deleteAllBlogs.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteAllBlogs.fulfilled, (state) => {
        state.deleting = false;
        state.success = true;
        state.blogs = [];
        state.currentBlog = null;
        state.stats = {
          totalBlogs: 0,
          publishedBlogs: 0,
          draftBlogs: 0,
          totalViews: 0,
          totalLikes: 0,
          publishRate: 0,
          avgReadTime: 0
        };
      })
      .addCase(deleteAllBlogs.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Get Blog Stats
      .addCase(getBlogStats.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBlogStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(getBlogStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Increment Blog Views
      .addCase(incrementBlogViews.pending, (state) => {
        // Don't show loading for views - it's non-critical
      })
      .addCase(incrementBlogViews.fulfilled, (state, action) => {
        const { blogId, views } = action.payload;
        console.log('Views increment successful:', { blogId, views }); // Debug log
        
        // Update views in the blogs list
        const blogIndex = state.blogs.findIndex(blog => blog._id === blogId);
        if (blogIndex !== -1) {
          console.log('Updating blog at index:', blogIndex, 'from', state.blogs[blogIndex].views, 'to', views);
          state.blogs[blogIndex].views = views;
        }
        
        // Update views in current blog if it matches
        if (state.currentBlog && state.currentBlog._id === blogId) {
          state.currentBlog.views = views;
        }
      })
      .addCase(incrementBlogViews.rejected, (state, action) => {
        console.error('Views increment failed:', action.payload);
        // Don't show error for views - it's non-critical
      })

      // Clear Error
      .addCase(clearError, (state) => {
        state.error = null;
      })

      // Clear Success
      .addCase(clearSuccess, (state) => {
        state.success = false;
      });
  },
});

export const { 
  setCurrentBlog, 
  clearCurrentBlog, 
  setFilters, 
  clearFilters,
  updateBlogInList 
} = blogSlice.actions;

export default blogSlice.reducer;